import { Injectable, inject } from '@angular/core';
import { UrlTree } from '@angular/router';
import { catchError, filter, firstValueFrom, of, timeout } from 'rxjs';
import { BebopUser } from 'src/app/interfaces/user';
import { AppStateService } from './app-state.service';
import { AuthenticationService } from './authentication.service';
import { GlobalService } from './global.service';
import { SecureStorageService } from './secure-storage.service';
import { UserService } from './user.service';

@Injectable({ providedIn: 'root' })
export class AuthGuardPolicyService {
  private appStateService = inject(AppStateService);
  private userService = inject(UserService);
  private storage = inject(SecureStorageService);
  private authService = inject(AuthenticationService);
  private globalService = inject(GlobalService);

  async resolveAccessDecision(currentUserUid: string | undefined, audience: 'client' | 'worker'): Promise<boolean | string | UrlTree> {
    if (!currentUserUid) {
      return '/public/account/login';
    }

    await this.userService.setCurrentUserId();

    const storedUser = await this.storage.getObject<{ uid?: string }>('user');
    if (storedUser && storedUser.uid && storedUser.uid !== currentUserUid) {
      await this.authService.storeUserLocally();
      return '/public/account/login';
    }

    const maintenanceMode = await firstValueFrom(this.globalService.getMaintenanceMode());
    if (maintenanceMode) {
      return '/public/maintenance';
    }

    let profile: BebopUser | undefined;

    try {
      profile = await this.userService.getUserProfileFromServer();
    } catch {
      profile = undefined;
    }

    if (!profile) {
      profile = await firstValueFrom(
        this.userService.getUserProfile().pipe(
          timeout(5000),
          catchError(() => of(undefined)),
          filter((p): p is BebopUser => p !== undefined)
        )
      );
    }

    if (!profile) {
      return '/public/account/login';
    }

    if (profile.is_banned) {
      return '/public/account/banned';
    }

    if (profile.registration_step === 2) {
      return this.appStateService.createUrlTree('/public/account/profile', { audience });
    }

    if (!profile.is_active) {
      return this.appStateService.createUrlTree('/public/account/waiting', { audience });
    }

    if (profile.setting_darkmode) {
      this.appStateService.applyDarkMode(true);
    } else {
      this.appStateService.applyDarkMode(false);
    }

    return true;
  }
}
